import React from 'react';
import { PrintDocumentTitle } from '@/components/shared/print';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { formatNumber } from '@/lib/utils';
import type { MaterialPlanningItem } from '@/shared/schemas';

interface MaterialPlanningPrintContentProps {
    items: MaterialPlanningItem[];
    periodLabel?: string;
    statusLabel?: string;
}

export function MaterialPlanningPrintContent({ items, periodLabel, statusLabel }: MaterialPlanningPrintContentProps) {
    const printDate = new Date();
    
    return (
        <div className="p-[10mm] w-full bg-white text-black text-[11px] leading-tight print:exact-colors print:m-0 mx-auto min-h-[210mm] kh-print-doc" id="print-area">
            {/* 3.1 Company Header & ISO Badge */}
            <div className="flex items-start justify-between mb-6">
                <div className="flex gap-4 items-center">
                    {/* Placeholder Logo */}
                    <div className="w-16 h-16 border-2 border-slate-800 flex items-center justify-center font-bold text-xl rounded">
                        APV
                    </div>
                    <div>
                        <h2 className="font-bold text-lg uppercase">CÔNG TY CỔ PHẦN APV</h2>
                        <p className="text-sm">Lô A, KCN Tân Bình, P.Tây Thạnh, Q.Tân Phú, TP.HCM</p>
                    </div>
                </div>
                
            </div>

            {/* 3.2 Title (CENTERED, NO QR) */}
            <PrintDocumentTitle
                title="KẾ HOẠCH VẬT TƯ"
                subtitle="Material Requirement Planning"
                date={format(printDate, 'dd/MM/yyyy HH:mm', { locale: vi })}
                dateLabel="Ngày in"
                extraMeta={
                    <span>
                        Kỳ báo cáo: <strong className="text-black">{periodLabel || 'Tất cả'}</strong> • Trạng thái: <strong className="text-black">{statusLabel || 'Tất cả'}</strong>
                    </span>
                }
            />


            {/* 3.3 Data Table */}
            <table className="w-full border-collapse border border-slate-800 text-[10pt] mb-8 print:exact-colors">
                <thead>
                    <tr className="bg-slate-100">
                        <th className="border border-slate-800 py-2 px-3 text-center w-10">STT</th>
                        <th className="border border-slate-800 py-2 px-3 text-left">Tên vật tư</th>
                        <th className="border border-slate-800 py-2 px-3 text-center w-16">ĐVT</th>
                        <th className="border border-slate-800 py-2 px-3 text-right w-24">Nhu cầu</th>
                        <th className="border border-slate-800 py-2 px-3 text-right w-24">Tồn kho</th>
                        <th className="border border-slate-800 py-2 px-3 text-right w-24 text-rose-700">Thiếu hụt</th>
                        <th className="border border-slate-800 py-2 px-3 text-center w-24">ĐH liên quan</th>
                        <th className="border border-slate-800 py-2 px-3 text-center w-28">Trạng thái</th>
                        <th className="border border-slate-800 py-2 px-3 text-left w-32">Ghi chú</th>
                    </tr>
                </thead>
                <tbody>
                    {items.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="border border-slate-800 py-6 px-3 text-center text-slate-500 italic">
                                Không có dữ liệu vật tư
                            </td>
                        </tr>
                    ) : (
                        items.map((item, index) => {
                            const neededQty = item.neededQty || item.requiredQty || 0;
                            const stockQty = item.stockQty || 0;
                            const shortageQty = item.shortageQty || 0;
                            
                            return (
                                <tr key={item.id} className="print:break-inside-avoid hover:bg-slate-50">
                                    <td className="border border-slate-800 py-1.5 px-3 text-center">{index + 1}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 font-medium">{item.materialName}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-center">{item.unit || 'Kg'}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-right font-medium">{formatNumber(neededQty)}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-right">{formatNumber(stockQty)}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-right font-bold text-rose-700">{formatNumber(shortageQty)}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-center">
                                        {item.relatedPo?.join(', ') || '-'}
                                    </td>
                                    <td className="border border-slate-800 py-1.5 px-3 text-center text-[9pt]">{item.status}</td>
                                    <td className="border border-slate-800 py-1.5 px-3 border-dotted"></td>
                                </tr>
                            );
                        })
                    )}
                </tbody>
            </table>

            {/* 3.4 Khối Chữ ký */}
            <div className="grid grid-cols-4 gap-4 text-center mt-8 print:break-inside-avoid">
                <div>
                    <div className="font-bold text-[11pt]">Người lập biểu</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Kế toán kho</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Trưởng bộ phận</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
                <div>
                    <div className="font-bold text-[11pt]">Giám đốc</div>
                    <div className="text-[9pt] italic mt-1 text-slate-600">(Ký, ghi rõ họ tên)</div>
                    <div className="h-24"></div>
                </div>
            </div>
        </div>
    );
}
